import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getExhibitionColorInfo, type ExhibitionColorMap } from '@/features/exhibitions/utils/exhibitionColors';
import chevronIcon from '@/assets/svg/chevron_icon.svg';
import draftIcon from '@/assets/svg/draft_icon.svg';
import checkIconCircle from '@/assets/svg/check_icon_circle.svg';
import plusIcon from '@/assets/svg/plus_icon.svg';
import trashIcon from '@/assets/svg/trash_icon.svg';

interface ExhibitionFormFieldsProps {
    status: 'draft' | 'published';
    onStatusChange: (status: 'draft' | 'published') => void;
    color: string;
    onColorChange: (color: string) => void;
    order: number;
    onOrderChange: (order: number) => void;
    selectedImage: File | null;
    imagePreview: string | null;
    onImageSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onImageDelete?: () => void;
    title: string;
    onTitleChange: (title: string) => void;
    description: string;
    onDescriptionChange: (description: string) => void;
    /** Description de la vignette (texte alternatif), affichée une fois l'image chargée. */
    coverAlt?: string;
    onCoverAltChange?: (coverAlt: string) => void;
    statusDropdownOpen: boolean;
    onStatusDropdownToggle: () => void;
    colorDropdownOpen: boolean;
    onColorDropdownToggle: () => void;
    orderDropdownOpen: boolean;
    onOrderDropdownToggle: () => void;
    colorMap: ExhibitionColorMap;
    /** Couleurs proposées dans le select (issues du musée) */
    availableColors: string[];
    totalCount: number; // Nombre total d'expositions
    onSave?: () => void;
    onCancel?: () => void;
    saving?: boolean;
    saveLabel?: string;
    onDelete?: () => void;
    /** Force le remontage des champs texte au changement de langue */
    formLanguage?: string;
}

export const ExhibitionFormFields: React.FC<ExhibitionFormFieldsProps> = ({
    status,
    onStatusChange,
    color,
    onColorChange,
    order,
    onOrderChange,
    selectedImage: _selectedImage,
    imagePreview,
    onImageSelect,
    onImageDelete,
    title,
    onTitleChange,
    description,
    onDescriptionChange,
    coverAlt = '',
    onCoverAltChange,
    statusDropdownOpen,
    onStatusDropdownToggle,
    colorDropdownOpen,
    onColorDropdownToggle,
    orderDropdownOpen,
    onOrderDropdownToggle,
    colorMap,
    availableColors,
    totalCount,
    onSave,
    onCancel,
    saving = false,
    saveLabel,
    onDelete,
    formLanguage,
}) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const titleTextareaRef = useRef<HTMLTextAreaElement>(null);
    const descriptionTextareaRef = useRef<HTMLTextAreaElement>(null);

    const handleImageClick = () => {
        fileInputRef.current?.click();
    };

    const handleImageModify = (e: React.MouseEvent) => {
        e.stopPropagation();
        fileInputRef.current?.click();
    };

    const handleImageDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onImageDelete) {
            onImageDelete();
        }
    };

    // Auto-resize pour le textarea du titre
    useEffect(() => {
        const textarea = titleTextareaRef.current;
        if (textarea) {
            // Réinitialiser la hauteur pour recalculer
            textarea.style.height = 'auto';
            // Définir la hauteur en fonction du contenu
            textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
        }
    }, [title]);

    // Auto-resize pour le textarea de la description
    useEffect(() => {
        const textarea = descriptionTextareaRef.current;
        if (textarea) {
            // Réinitialiser la hauteur pour recalculer
            textarea.style.height = 'auto';
            // Définir la hauteur en fonction du contenu (sans limite max pour la description)
            textarea.style.height = `${textarea.scrollHeight}px`;
        }
    }, [description]);

    return (
        <>
            <div className="form-header">
                <div className="form-controls">
                    <div className="form-control-group">
                        <label>{t('exhibitions.status')}</label>
                        <div className="dropdown">
                            <button
                                className="dropdown-button"
                                onClick={onStatusDropdownToggle}
                            >
                                <div className="status-selector-content">
                                    {status === 'draft' ? (
                                        <img src={draftIcon} alt="" className="status-icon" />
                                    ) : (
                                        <img src={checkIconCircle} alt="" className="status-icon" />
                                    )}
                                    <span>{status === 'draft' ? t('exhibitions.draft') : t('exhibitions.published')}</span>
                                </div>
                                <img src={chevronIcon} alt="" className={`chevron-icon ${statusDropdownOpen ? 'rotated' : ''}`} />
                            </button>
                            {statusDropdownOpen && (
                                <div className="dropdown-menu status-dropdown-menu">
                                    <button
                                        className={`status-option ${status === 'draft' ? 'active' : ''}`}
                                        onClick={() => { onStatusChange('draft'); onStatusDropdownToggle(); }}
                                    >
                                        <div className="status-selector-content">
                                            <img src={draftIcon} alt="" className="status-icon" />
                                            <div className="status-text">
                                                <span className="status-title">{t('exhibitions.draft')}</span>
                                                <span className="status-description">{t('exhibitions.draftDescription')}</span>
                                            </div>
                                        </div>
                                    </button>
                                    <button
                                        className={`status-option ${status === 'published' ? 'active' : ''}`}
                                        onClick={() => { onStatusChange('published'); onStatusDropdownToggle(); }}
                                    >
                                        <div className="status-selector-content">
                                            <img src={checkIconCircle} alt="" className="status-icon" />
                                            <div className="status-text">
                                                <span className="status-title">{t('exhibitions.published')}</span>
                                                <span className="status-description">{t('exhibitions.publishedDescription')}</span>
                                            </div>
                                        </div>
                                    </button>
                                    {onDelete && (
                                        <button
                                            className="status-option delete-option"
                                            type="button"
                                            onClick={() => {
                                                onDelete();
                                                onStatusDropdownToggle();
                                            }}
                                        >
                                            <div className="status-selector-content">
                                                <img src={trashIcon} alt="" className="status-icon" />
                                                <div className="status-text">
                                                    <span className="status-title">{t('exhibitions.moveToTrash')}</span>
                                                    <span className="status-description">{t('exhibitions.moveToTrashDescription')}</span>
                                                </div>
                                            </div>
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="form-control-group">
                        <label>{t('exhibitions.color')}</label>
                        <div className="dropdown">
                            <button
                                className="dropdown-button"
                                onClick={onColorDropdownToggle}
                            >
                                <div className="color-selector-content">
                                    <div
                                        className="color-swatch"
                                        style={{ backgroundColor: colorMap[color]?.hex ?? getExhibitionColorInfo(color).hex }}
                                    />
                                    <span>{colorMap[color]?.name ?? getExhibitionColorInfo(color).name}</span>
                                </div>
                                <img src={chevronIcon} alt="" className={`chevron-icon ${colorDropdownOpen ? 'rotated' : ''}`} />
                            </button>
                            {colorDropdownOpen && (
                                <div className="dropdown-menu">
                                    {availableColors.map((colorCode) => (
                                        <button
                                            key={colorCode}
                                            onClick={() => {
                                                onColorChange(colorCode);
                                                onColorDropdownToggle();
                                            }}
                                        >
                                            <div className="color-selector-content">
                                                <div
                                                    className="color-swatch"
                                                    style={{ backgroundColor: colorMap[colorCode]?.hex ?? getExhibitionColorInfo(colorCode).hex }}
                                                />
                                                <span>{colorMap[colorCode]?.name ?? getExhibitionColorInfo(colorCode).name}</span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="form-control-group">
                        <label>{t('exhibitions.orderInList')}</label>
                        <div className="dropdown">
                            <button
                                id="order-dropdown-button"
                                className="dropdown-button"
                                onClick={onOrderDropdownToggle}
                            >
                                <span>{order}</span>
                                <img src={chevronIcon} alt="" className={`chevron-icon ${orderDropdownOpen ? 'rotated' : ''}`} />
                            </button>
                            {orderDropdownOpen && (
                                <div className="dropdown-menu">
                                    {Array.from({ length: totalCount }, (_, i) => i + 1).map((num) => (
                                        <button key={num} onClick={() => { onOrderChange(num); onOrderDropdownToggle(); }}>
                                            {num}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {onSave && onCancel && (
                    <div className="form-actions">
                        <button
                            className="save-draft-btn"
                            onClick={onSave}
                            disabled={saving}
                        >
                            {saving ? t('common.loading') : (saveLabel || t('exhibitions.saveDraft'))}
                        </button>
                        <button className="cancel-btn" onClick={onCancel}>
                            {t('common.cancel')}
                        </button>
                    </div>
                )}
            </div>

            <div className="form-content">
                <div className="image-upload-section">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={onImageSelect}
                        style={{ display: 'none' }}
                    />
                    <div className={`image-upload-box ${imagePreview ? 'has-image' : ''}`} onClick={handleImageClick}>
                        {imagePreview ? (
                            <>
                                <img src={imagePreview} alt="Preview" className="image-preview" />
                                <div className="upload-overlay">
                                    <div className="image-action-buttons">
                                        <button
                                            type="button"
                                            className="image-action-btn modify-btn"
                                            onClick={handleImageModify}
                                        >
                                            {t('common.edit')}
                                        </button>
                                        <button
                                            type="button"
                                            className="image-action-btn delete-btn"
                                            onClick={handleImageDelete}
                                        >
                                            {t('common.delete')}
                                        </button>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="image-upload-box-content">
                                    <img src={plusIcon} alt="Plus" className="plus-icon" />
                                </div>
                                <span>{t('exhibitions.addImage')}</span>
                            </>
                        )}
                    </div>
                </div>

                <div className="text-inputs-section">
                    <textarea
                        key={formLanguage ? `title-${formLanguage}` : undefined}
                        ref={titleTextareaRef}
                        className="title-input"
                        placeholder={t('exhibitions.addTitle')}
                        value={title}
                        onChange={(e) => {
                            onTitleChange(e.target.value);
                            // Auto-resize immédiat
                            const textarea = e.target as HTMLTextAreaElement;
                            textarea.style.height = 'auto';
                            textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
                        }}
                        rows={1}
                    />
                    <textarea
                        key={formLanguage ? `description-${formLanguage}` : undefined}
                        ref={descriptionTextareaRef}
                        className="description-input"
                        placeholder={t('exhibitions.addDescription')}
                        value={description}
                        onChange={(e) => {
                            onDescriptionChange(e.target.value);
                            // Auto-resize immédiat
                            const textarea = e.target as HTMLTextAreaElement;
                            textarea.style.height = 'auto';
                            textarea.style.height = `${textarea.scrollHeight}px`;
                        }}
                        rows={4}
                    />
                    {imagePreview && onCoverAltChange && (
                        <div className="cover-alt-field">
                            <label htmlFor="exhibition-cover-alt" className="cover-alt-field__label">
                                {t('exhibitions.coverAlt.label')}
                            </label>
                            <p id="exhibition-cover-alt-hint" className="cover-alt-field__hint">
                                {t('exhibitions.coverAlt.hint')}
                            </p>
                            <textarea
                                key={formLanguage ? `cover-alt-${formLanguage}` : undefined}
                                id="exhibition-cover-alt"
                                className="cover-alt-field__input"
                                aria-describedby="exhibition-cover-alt-hint"
                                rows={2}
                                value={coverAlt}
                                onChange={(e) => onCoverAltChange(e.target.value)}
                            />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

